import type { BigNumberish } from 'ethers';
import { BigNumber, constants } from 'ethers';

import type { UniswapV2Pair } from '../lib/contracts';
import { UniswapV2PairFactory } from '../lib/contracts';
import {
  STARS_ETH_CONTRACT_ADDRESS,
  weiToEth,
} from '../lib/smart-contract-utils';
import { useWeb3State } from '.';
import { useContract } from './contract.hooks';

export const useStarsEthLpTokenContract = (
  hardOverrideProvider = false,
  bsctest = false,
) => {
  const { walletDetails } = useWeb3State();
  const { contract: baseContract, ...walletInfo } = useContract(
    UniswapV2PairFactory,
    STARS_ETH_CONTRACT_ADDRESS,
    STARS_ETH_CONTRACT_ADDRESS[walletDetails.chain.type],
    walletDetails.chain.type,
    hardOverrideProvider,
  );
  const contract = baseContract as UniswapV2Pair | undefined;
  const approve = async (spender: string, amount: BigNumberish) => {
    if (!contract) {
      return false;
    }

    const trx = await contract.approve(spender, amount);
    await trx.wait();
    return true;
  };

  return {
    walletInfo,
    initialized: contract !== undefined,
    getStarsEthTotalSupply: async () => {
      if (!contract) {
        return BigNumber.from(0);
      }
      try {
        return await contract.totalSupply();
      } catch (e) {
        window.Sentry?.captureException(e, {
          tags: {
            file: 'lp-token-contract.hooks.ts',
            function: 'getStarsEthTotalSupply',
          },
        });
        return BigNumber.from(0);
      }
    },
    getStarsEthReserves: async () => {
      if (!contract) {
        return [BigNumber.from(0)];
      }
      try {
        return await contract.getReserves();
      } catch (e) {
        window.Sentry?.captureException(e, {
          tags: {
            file: 'lp-token-contract.hooks.ts',
            function: 'getStarsEthReserves',
          },
        });
        return [BigNumber.from(0)];
      }
    },
    allowance: async (owner: string, spender: string) => {
      if (!contract) {
        return '0';
      }

      try {
        const allowanceMantissa = await contract.allowance(owner, spender);
        return weiToEth(allowanceMantissa);
      } catch (e) {
        return '0';
      }
    },
    approve: async (spender: string, amount: BigNumberish) => {
      if (!contract) {
        return false;
      }
      try {
        const trx = await contract.approve(spender, amount);
        await trx.wait();
        return true;
      } catch (e) {
        window.Sentry?.captureException(e, {
          tags: {
            file: 'lp-token-contract.hooks.ts',
            function: 'approve',
          },
        });
        return false;
      }
    },
    approveInfinite: async (spender: string) => {
      return approve(spender, constants.MaxUint256);
    },
    getBalance: async (address: string) => {
      if (!contract) {
        return;
      }
      const balance = await contract.balanceOf(address);
      return balance;
    },
  };
};
